import * as THREE from 'three'
import { ATLAS_COLS, ATLAS_ROWS, MAX_FRAMES_PER_MESH } from './types'

/**
 * The V3 overlay shader.
 *
 * V2 derives one UV per fragment from the mesh's shape — `atan(pos.x, pos.z)` around the
 * bounding box's axis — and samples a single canvas that represents the mesh unrolled.
 * That ties the section's coordinates to the geometry underneath it, which is why equal
 * angles stop being equal distances the moment the cross-section is not round.
 *
 * Here the coordinates come from the SECTION instead. Each frame carries a matrix that
 * takes a mesh-local position into the frame's own space, where the section is the unit
 * rectangle centred on the origin. Nothing about the mesh enters the calculation, so a
 * 40 mm section is 40 mm of surface on a cup, a slide flat, or a chamfer alike.
 *
 * Frames share one tiled atlas rather than one sampler each: GLSL cannot index an array of
 * samplers with a loop variable in a way that is safe across targets, and a 4 × 2 grid of
 * tiles carries MAX_FRAMES_PER_MESH sections through a single texture unit.
 */

export interface FrameUniformData {
  /** Mesh-local → frame space. */
  matrix: THREE.Matrix4
  /** Half-extent divisor: the section's width/height in METRES. */
  size: THREE.Vector2
  /** Projection throw in metres. */
  depth: number
  /** Wrap radius in metres; 0 is a flat decal. */
  wrapRadius: number
  /** cos of the angle limit: the surface must face the projector at least this squarely. */
  minCos: number
  /** Index of this frame's tile in the atlas. */
  tile: number
}

/**
 * How an engraved section LOOKS, as opposed to where it lands.
 *
 * The frame projector originally painted a flat two-tone mask, which is right for the
 * admin portal — an author needs to see a placeholder, not a finished engraving. In the
 * Customizer it was plainly wrong: every section rendered in the authoring accent colour
 * regardless of the material the profile selected, so a steel, a sandblast and a wood-burn
 * section were indistinguishable.
 *
 * These are the same quantities the unrolled path derives in
 * applyDesignToTargetObjectMaterial, and they are resolved per MESH there (from the first
 * section on it), so they are scalars here rather than per-frame arrays. Textures are
 * passed in rather than loaded, because they are Customizer assets and this module
 * deliberately depends on nothing but `three`.
 */
export interface EngravingAppearance {
  /** Print the artwork's own RGB instead of repainting it with a material. */
  colorMode: boolean
  /** Greyscale engraving brightness. Hue comes from materialTint. */
  overlayTint: THREE.Color
  /** Material hue, multiplied over the tint. White for materials with no hue. */
  materialTint: THREE.Color
  /** Grain tile for frosted and custom materials, or null. */
  materialMap?: THREE.Texture | null
  /** Physical size of one grain tile, millimetres. */
  materialTileMm?: number
  useMaterialTexture?: boolean
  /** Inner bevel depth as a fraction of the tile. 0 disables bevel and outer shadow. */
  bevelOffset?: number
  bevelShadow?: number
  bevelHighlight?: number
  /** Target roughness over the engraved area. 0 leaves the surface alone. */
  materialRoughness?: number
  /** How far toward the metalness map the engraved area moves. 0 leaves it alone. */
  materialMetalness?: number
  metalnessMap?: THREE.Texture | null
  /** Bare-metal finish: the engraving becomes brushed metal rather than a tint. */
  useMetalFinish?: boolean
  metalAlbedo?: THREE.Color
  metalRoughness?: number
  brushContrast?: number
  brushRoughnessVariation?: number
  brushedMap?: THREE.Texture | null
  /** Physical size of one brushed tile, millimetres. */
  brushTileMm?: number
}

const CACHE_KEY = 'productProfileV3_projector'

/**
 * A 1x1 white texture for the appearance samplers.
 *
 * Their branches are switched off by uniform on the authoring side, but a sampler left
 * null is still bound to unit 0 — where the atlas lives — and some drivers warn about two
 * samplers of the same type pointing at one unit. One shared pixel costs nothing.
 */
let blankTexture: THREE.DataTexture | null = null
function blank(): THREE.Texture {
  if (!blankTexture) {
    blankTexture = new THREE.DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1)
    blankTexture.needsUpdate = true
  }
  return blankTexture
}

/** Uniform block shape, sized once so the shader never recompiles as sections come and go. */
function emptyFrameUniforms() {
  return {
    matrices: Array.from({ length: MAX_FRAMES_PER_MESH }, () => new THREE.Matrix4()),
    sizes: Array.from({ length: MAX_FRAMES_PER_MESH }, () => new THREE.Vector2(1, 1)),
    depths: new Array(MAX_FRAMES_PER_MESH).fill(0),
    wraps: new Array(MAX_FRAMES_PER_MESH).fill(0),
    minCos: new Array(MAX_FRAMES_PER_MESH).fill(0),
    tiles: new Array(MAX_FRAMES_PER_MESH).fill(0),
  }
}

const VERTEX_DECLARATIONS = /* glsl */ `
	varying vec3 vV3LocalPos;
	varying vec3 vV3LocalNormal;
	varying vec3 vV3WorldPos;
	varying vec3 vV3WorldNormal;
`

const VERTEX_ASSIGNMENTS = /* glsl */ `
	vV3LocalPos = position;
	vV3LocalNormal = normal;
	vV3WorldPos = (modelMatrix * vec4(position, 1.0)).xyz;
	vV3WorldNormal = normalize(mat3(modelMatrix) * normal);
`

const FRAGMENT_DECLARATIONS = /* glsl */ `
	varying vec3 vV3LocalPos;
	varying vec3 vV3LocalNormal;
	varying vec3 vV3WorldPos;
	varying vec3 vV3WorldNormal;

	uniform sampler2D v3Atlas;
	uniform int   v3FrameCount;
	uniform mat4  v3FrameMatrix[${MAX_FRAMES_PER_MESH}];
	uniform vec2  v3FrameSize[${MAX_FRAMES_PER_MESH}];
	uniform float v3FrameDepth[${MAX_FRAMES_PER_MESH}];
	uniform float v3FrameWrap[${MAX_FRAMES_PER_MESH}];
	uniform float v3FrameMinCos[${MAX_FRAMES_PER_MESH}];
	uniform float v3FrameTile[${MAX_FRAMES_PER_MESH}];
	uniform vec3  v3TintLight;
	uniform vec3  v3TintDark;
	uniform vec3  v3SelectTint;
	uniform float v3Highlight[${MAX_FRAMES_PER_MESH}];

	// Engraving appearance. All inert at their defaults, which is what keeps the
	// authoring path (v3PaintMode 0) byte-identical to what it rendered before.
	uniform float v3PaintMode;
	uniform float v3UseColorMode;
	uniform vec3  v3OverlayTint;
	uniform vec3  v3MaterialTint;
	uniform sampler2D v3MaterialMap;
	uniform float v3UseMaterialTexture;
	uniform float v3MaterialTileMm;
	uniform float v3BevelOffset;
	uniform float v3BevelShadow;
	uniform float v3BevelHighlight;
	uniform float v3MaterialRoughness;
	uniform float v3MaterialMetalness;
	uniform sampler2D v3MetalnessMap;
	uniform float v3UseMetalFinish;
	uniform vec3  v3MetalAlbedo;
	uniform float v3MetalRoughness;
	uniform float v3BrushContrast;
	uniform float v3BrushRoughnessVariation;
	uniform sampler2D v3BrushedMap;
	uniform float v3BrushTileMm;

	const vec2 V3_ATLAS_GRID = vec2(${ATLAS_COLS}.0, ${ATLAS_ROWS}.0);

	/*
	 * Tile-local UV to a point in the shared atlas.
	 *
	 * The clamp is what keeps a bevel tap — which deliberately samples a little outside
	 * the artwork — from reading a neighbouring section's tile and drawing a fringe from
	 * someone else's logo.
	 */
	vec2 v3AtlasUv(vec2 tileUv, float tile) {
		vec2 cell = vec2(mod(tile, V3_ATLAS_GRID.x), floor(tile / V3_ATLAS_GRID.x));
		return (cell + clamp(tileUv, 0.002, 0.998)) / V3_ATLAS_GRID;
	}

	/*
	 * Place a mesh-local point inside one frame.
	 *
	 * Returns the section's own UV in .xy, and in .z how far the point sits from the
	 * projected surface — the value the depth throw is tested against. A flat frame
	 * measures that along the frame's forward axis; a wrapped one measures it radially
	 * from the axis sitting wrapRadius behind the origin, so a curved wall keeps a
	 * constant projection thickness instead of drifting off the surface at its edges.
	 */
	vec3 v3Project(vec3 p, float radius, vec2 size) {
		float u;
		float dist;

		if (radius > 0.0) {
			// Arc length about the axis at (0, y, -radius), which is what makes a wrapped
			// section's width a true distance across the curved surface.
			vec2 radial = vec2(p.x, p.z + radius);
			float theta = atan(radial.x, radial.y);
			u = (theta * radius) / size.x + 0.5;
			dist = length(radial) - radius;
		} else {
			u = p.x / size.x + 0.5;
			dist = p.z;
		}

		// Canvas rows run downward and the atlas texture is uploaded with flipY off, so
		// the frame's +Y has to land on the tile's top edge.
		float v = 0.5 - p.y / size.y;
		return vec3(u, v, dist);
	}

	/*
	 * How squarely this surface faces the projector: 1 is head-on, 0 is edge-on, below 0
	 * is the far wall.
	 *
	 * This is the number the stretch is governed by. A projector casts its artwork along
	 * one direction, so the surface receives it magnified by 1/facing — which is why an
	 * unbounded projection smears down a side wall instead of stopping at the edge. The
	 * caller compares it against the frame's angle limit rather than merely against zero.
	 *
	 * A wrapped frame measures against the curved surface it follows, not a fixed plane,
	 * so a full cylinder wrap reads as head-on the whole way round.
	 */
	float v3Facing(vec3 n, vec3 p, float radius) {
		if (radius > 0.0) {
			vec3 outward = normalize(vec3(p.x, 0.0, p.z + radius));
			return dot(n, outward);
		}
		return n.z;
	}
`

/**
 * Loop over the frames and keep the last one that claims the fragment. Sections on one
 * mesh are not expected to overlap; where an author does overlap them, later-listed wins,
 * which matches the order they read in the panel.
 */
const FRAGMENT_OVERLAY = /* glsl */ `
	// Declared at main()'s scope, NOT inside a block: the metalness, roughness and tone
	// mapping chunks below are separate injections further down the same function, and
	// they need to know whether this fragment was engraved and how the brush fell.
	float v3OverlayAlpha = 0.0;
	float v3BrushValue = 0.5;
	vec2 v3TileUvOut = vec2(0.0);

	{
		vec4 v3Overlay = vec4(0.0);
		float v3Hi = 0.0;
		// The frame that claimed this fragment, kept so the appearance pass below can
		// take more samples from the same tile. Recorded for any fragment INSIDE a
		// frame, not only a painted one, because the outer shadow is by definition
		// drawn where there is no artwork.
		bool v3Inside = false;
		float v3Tile = 0.0;
		vec2 v3TileUv = vec2(0.0);
		vec2 v3SizeMm = vec2(1.0);

		// Only paint what the viewer can actually see, so a section on the near wall does
		// not read through a thin or transparent part from behind.
		vec3 v3ViewDir = normalize(cameraPosition - vV3WorldPos);
		if (dot(vV3WorldNormal, v3ViewDir) > 0.0) {
			for (int i = 0; i < ${MAX_FRAMES_PER_MESH}; i++) {
				if (i >= v3FrameCount) break;

				vec3 p = (v3FrameMatrix[i] * vec4(vV3LocalPos, 1.0)).xyz;
				vec3 n = normalize(mat3(v3FrameMatrix[i]) * vV3LocalNormal);

				vec3 uvd = v3Project(p, v3FrameWrap[i], v3FrameSize[i]);
				if (uvd.x < 0.0 || uvd.x > 1.0 || uvd.y < 0.0 || uvd.y > 1.0) continue;
				if (abs(uvd.z) > v3FrameDepth[i] * 0.5) continue;

				// Stop where the surface turns away, instead of smearing onto it. The band
				// above the limit is faded rather than cut so a rounded edge ends softly
				// and does not crawl as the camera moves.
				float facing = v3Facing(n, p, v3FrameWrap[i]);
				float minCos = v3FrameMinCos[i];
				if (facing <= minCos) continue;
				float edge = smoothstep(minCos, minCos + 0.12, facing);

				v3Inside = true;
				v3Tile = v3FrameTile[i];
				v3TileUv = uvd.xy;
				v3TileUvOut = uvd.xy;
				v3SizeMm = v3FrameSize[i] * 1000.0;

				vec4 sampled = texture2D(v3Atlas, v3AtlasUv(uvd.xy, v3FrameTile[i]));
				sampled.a *= edge;
				if (sampled.a > 0.01) {
					v3Overlay = sampled;
					v3Hi = v3Highlight[i];
				}
			}
		}

		if (v3PaintMode > 0.5) {
			// ENGRAVING. The same treatment the unrolled path applies in
			// applyDesignToTargetObjectMaterial, reading from the section's tile instead
			// of from the mesh's unrolled canvas. Keeping it identical is the point:
			// a gold section has to look gold whichever route put it on the product.
			float a = v3Overlay.a;

			if (a > 0.0) {
				vec3 colored = v3UseColorMode > 0.5 ? v3Overlay.rgb : v3OverlayTint * v3MaterialTint;

				// Bare metal: lasering a coated part strips the coating, so the engraving
				// is brushed metal rather than a tint.
				if (v3UseMetalFinish > 0.5 && v3UseColorMode < 0.5) {
					v3BrushValue = texture2D(v3BrushedMap, v3TileUv * (v3SizeMm / max(v3BrushTileMm, 0.001))).r;
					colored = v3MetalAlbedo;
				}

				// Frosted and custom grain. Tiled by the section's REAL millimetres, so
				// the grain keeps one physical scale whatever size the section is.
				if (v3UseMaterialTexture > 0.5) {
					vec2 gUv = v3TileUv * (v3SizeMm / max(v3MaterialTileMm, 0.001));
					colored *= texture2D(v3MaterialMap, gUv).rgb;
				}

				diffuseColor.rgb = mix(diffuseColor.rgb, colored, a);

				// Inner bevel: dark fringe where the groove drops away from the surface,
				// light fringe where its floor catches the light.
				if (v3BevelOffset > 0.0) {
					float aUp = texture2D(v3Atlas, v3AtlasUv(v3TileUv + vec2(0.0, -v3BevelOffset), v3Tile)).a;
					float aDown = texture2D(v3Atlas, v3AtlasUv(v3TileUv + vec2(0.0, v3BevelOffset), v3Tile)).a;
					float topFringe = a * (1.0 - step(0.1, aUp));
					float bottomFringe = a * (1.0 - step(0.1, aDown));
					diffuseColor.rgb -= vec3(topFringe * v3BevelShadow);
					diffuseColor.rgb += vec3(bottomFringe * v3BevelHighlight);
				}

				v3OverlayAlpha = a;
			} else if (v3Inside && v3BevelOffset > 0.0) {
				// Outer shadow: the surface just below an engraved edge sits in its shade.
				float sA = texture2D(v3Atlas, v3AtlasUv(v3TileUv + vec2(0.0, -v3BevelOffset * 2.0), v3Tile)).a;
				sA = sA > 0.1 ? sA : 0.0;
				diffuseColor.rgb *= 1.0 - sA * v3BevelShadow * 0.25;
			}
		} else if (v3Overlay.a > 0.0) {
			// AUTHORING. The canvas carries coverage, not colour: it is drawn as a flat
			// mask and the visible colour comes from these uniforms. Which one is chosen
			// by the surface underneath, because an authoring overlay has to be seen and
			// a fixed tint cannot be: a light patch vanishes on a white tumbler, a dark
			// one vanishes on a black pistol, and the catalogue holds both. Picking on
			// luminance means the section always contrasts with whatever it sits on. The
			// label and border are knocked OUT of the mask rather than painted into it,
			// so they show the product through the patch and stay legible either way.
			float v3Lum = dot(diffuseColor.rgb, vec3(0.2126, 0.7152, 0.0722));
			vec3 paint = mix(v3TintLight, v3TintDark, step(0.32, v3Lum));
			paint = mix(paint, v3SelectTint, v3Hi);
			diffuseColor.rgb = mix(diffuseColor.rgb, paint, v3Overlay.a);
		}
	}
`

/** Engraved areas take on the material's metalness. Inert unless the Customizer asks. */
const FRAGMENT_METALNESS = /* glsl */ `
	if (v3PaintMode > 0.5 && v3OverlayAlpha > 0.0 && v3UseColorMode < 0.5) {
		if (v3MaterialMetalness > 0.0) {
			float steelM = texture2D(v3MetalnessMap, v3TileUvOut).r;
			metalnessFactor = mix(metalnessFactor, steelM, v3OverlayAlpha * v3MaterialMetalness);
		}
		if (v3UseMetalFinish > 0.5) {
			metalnessFactor = mix(metalnessFactor, 1.0, v3OverlayAlpha);
		}
	}
`

/** ...and its roughness, with the brush breaking the highlight into lines. */
const FRAGMENT_ROUGHNESS = /* glsl */ `
	if (v3PaintMode > 0.5 && v3OverlayAlpha > 0.0 && v3UseColorMode < 0.5) {
		if (v3MaterialRoughness > 0.0) {
			roughnessFactor = mix(roughnessFactor, v3MaterialRoughness, v3OverlayAlpha);
		}
		if (v3UseMetalFinish > 0.5) {
			float brushed = clamp(v3MetalRoughness + (v3BrushValue - 0.5) * 2.0 * v3BrushRoughnessVariation, 0.04, 1.0);
			roughnessFactor = mix(roughnessFactor, brushed, v3OverlayAlpha);
		}
	}
`

/**
 * Brush lines go on AFTER tone mapping.
 *
 * Bright metal reflections sit near white once ACES has run, so a swing applied at albedo
 * level disappears everywhere except the darker reflection band — the grain shows up on
 * part of the surface and not the rest.
 */
const FRAGMENT_TONEMAP = /* glsl */ `
	if (v3PaintMode > 0.5 && v3UseMetalFinish > 0.5 && v3UseColorMode < 0.5 && v3OverlayAlpha > 0.0) {
		gl_FragColor.rgb *= 1.0 + (v3BrushValue - 0.5) * 2.0 * v3BrushContrast * v3OverlayAlpha;
	}
`

/**
 * Wrap a mesh's material so it draws section overlays, keeping the original intact.
 *
 * The original is stashed on userData the first time through, so a mesh can be handed back
 * its authored surface when its last section is deleted — and so repeated edits clone from
 * the real material rather than compounding clones of clones.
 */
export function createProjectorMaterial(mesh: THREE.Mesh): THREE.MeshStandardMaterial {
  const existing = mesh.userData.v3Original as THREE.Material | undefined
  const base = (existing ??
    (Array.isArray(mesh.material) ? mesh.material[0] : mesh.material)) as THREE.MeshStandardMaterial
  if (!existing) mesh.userData.v3Original = base

  const material = base.clone() as THREE.MeshStandardMaterial
  const frames = emptyFrameUniforms()

  const uniforms = {
    v3Atlas: { value: null as THREE.Texture | null },
    v3FrameCount: { value: 0 },
    v3FrameMatrix: { value: frames.matrices },
    v3FrameSize: { value: frames.sizes },
    v3FrameDepth: { value: frames.depths },
    v3FrameWrap: { value: frames.wraps },
    v3FrameMinCos: { value: frames.minCos },
    v3FrameTile: { value: frames.tiles },
    v3Highlight: { value: new Array(MAX_FRAMES_PER_MESH).fill(0) },
    v3TintLight: { value: new THREE.Color(0xe6ebe0) },
    v3TintDark: { value: new THREE.Color(0x2b3325) },
    v3SelectTint: { value: new THREE.Color(0x79bc61) },

    // Appearance. Defaults are the authoring mask, so a material that is never given an
    // EngravingAppearance renders exactly as it did before any of this existed.
    v3PaintMode: { value: 0 },
    v3UseColorMode: { value: 0 },
    v3OverlayTint: { value: new THREE.Color(0xffffff) },
    v3MaterialTint: { value: new THREE.Color(0xffffff) },
    v3MaterialMap: { value: blank() },
    v3UseMaterialTexture: { value: 0 },
    v3MaterialTileMm: { value: 120 },
    v3BevelOffset: { value: 0 },
    v3BevelShadow: { value: 0 },
    v3BevelHighlight: { value: 0 },
    v3MaterialRoughness: { value: 0 },
    v3MaterialMetalness: { value: 0 },
    v3MetalnessMap: { value: blank() },
    v3UseMetalFinish: { value: 0 },
    v3MetalAlbedo: { value: new THREE.Color(0x000000) },
    v3MetalRoughness: { value: 0 },
    v3BrushContrast: { value: 0 },
    v3BrushRoughnessVariation: { value: 0 },
    v3BrushedMap: { value: blank() },
    v3BrushTileMm: { value: 120 },
  }

  material.customProgramCacheKey = () => CACHE_KEY
  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms)

    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>\n${VERTEX_DECLARATIONS}`)
      .replace('#include <worldpos_vertex>', `#include <worldpos_vertex>\n${VERTEX_ASSIGNMENTS}`)

    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\n${FRAGMENT_DECLARATIONS}`)
      // Appended rather than replacing <map_fragment>, so the mesh's own base colour
      // map — the thing the 3D Studio work just restored — is sampled by three's own
      // code and the overlay composites on top of it.
      .replace('#include <map_fragment>', `#include <map_fragment>\n${FRAGMENT_OVERLAY}`)
      // The engraved area takes the material's surface response, not just its colour.
      // Appended so three's own chunks run first and these adjust the result.
      .replace('#include <metalnessmap_fragment>', `#include <metalnessmap_fragment>\n${FRAGMENT_METALNESS}`)
      .replace('#include <roughnessmap_fragment>', `#include <roughnessmap_fragment>\n${FRAGMENT_ROUGHNESS}`)
      .replace('#include <tonemapping_fragment>', `#include <tonemapping_fragment>\n${FRAGMENT_TONEMAP}`)

    material.userData.v3Uniforms = uniforms
  }

  material.userData.v3Uniforms = uniforms
  material.needsUpdate = true
  return material
}

/**
 * Push the current frame set into a projector material's uniforms.
 *
 * `appearance` is optional and omitting it leaves the material in authoring mode — the
 * flat two-tone mask. The Customizer passes one so the engraving renders in the material
 * the profile selected rather than in the authoring accent.
 */
export function updateProjectorUniforms(
  material: THREE.Material,
  atlas: THREE.Texture,
  frames: FrameUniformData[],
  highlightIndex: number,
  appearance?: EngravingAppearance | null
): void {
  const u = material.userData?.v3Uniforms
  if (!u) return

  applyAppearance(u, appearance)

  u.v3Atlas.value = atlas
  u.v3FrameCount.value = Math.min(frames.length, MAX_FRAMES_PER_MESH)

  frames.slice(0, MAX_FRAMES_PER_MESH).forEach((frame, i) => {
    u.v3FrameMatrix.value[i].copy(frame.matrix)
    u.v3FrameSize.value[i].copy(frame.size)
    u.v3FrameDepth.value[i] = frame.depth
    u.v3FrameWrap.value[i] = frame.wrapRadius
    u.v3FrameMinCos.value[i] = frame.minCos
    u.v3FrameTile.value[i] = frame.tile
    u.v3Highlight.value[i] = i === highlightIndex ? 1 : 0
  })
}

/**
 * Copy an appearance into the uniform block, or switch back to the authoring mask.
 *
 * Every field is written on every call rather than only the ones that changed: switching
 * a profile from gold to sandblast has to clear the metal finish, and a half-updated
 * block renders a material that exists in neither profile.
 */
function applyAppearance(u: Record<string, { value: any }>, appearance?: EngravingAppearance | null): void {
  if (!appearance) {
    u.v3PaintMode.value = 0
    return
  }

  u.v3PaintMode.value = 1
  u.v3UseColorMode.value = appearance.colorMode ? 1 : 0
  u.v3OverlayTint.value.copy(appearance.overlayTint)
  u.v3MaterialTint.value.copy(appearance.materialTint)

  u.v3MaterialMap.value = appearance.materialMap ?? blank()
  u.v3UseMaterialTexture.value = appearance.useMaterialTexture && appearance.materialMap ? 1 : 0
  u.v3MaterialTileMm.value = appearance.materialTileMm ?? 120

  u.v3BevelOffset.value = appearance.bevelOffset ?? 0
  u.v3BevelShadow.value = appearance.bevelShadow ?? 0
  u.v3BevelHighlight.value = appearance.bevelHighlight ?? 0

  u.v3MaterialRoughness.value = appearance.materialRoughness ?? 0
  u.v3MaterialMetalness.value = appearance.materialMetalness ?? 0
  u.v3MetalnessMap.value = appearance.metalnessMap ?? blank()

  u.v3UseMetalFinish.value = appearance.useMetalFinish ? 1 : 0
  if (appearance.metalAlbedo) u.v3MetalAlbedo.value.copy(appearance.metalAlbedo)
  u.v3MetalRoughness.value = appearance.metalRoughness ?? 0
  u.v3BrushContrast.value = appearance.brushContrast ?? 0
  u.v3BrushRoughnessVariation.value = appearance.brushRoughnessVariation ?? 0
  u.v3BrushedMap.value = appearance.brushedMap ?? blank()
  u.v3BrushTileMm.value = appearance.brushTileMm ?? 120
}

/** Hand a mesh back the material it arrived with. */
export function restoreOriginalMaterial(mesh: THREE.Mesh): void {
  const original = mesh.userData.v3Original as THREE.Material | undefined
  if (!original) return
  const current = mesh.material as THREE.Material
  if (current !== original) current.dispose()
  mesh.material = original
}
